/**
 * AppHeaderContext
 *
 * Context for sharing header configuration across components.
 * Allows pages to set their title and navigation actions in the app header.
 *
 * CRITICAL: Context value is memoized to prevent infinite re-render loops.
 * Without memoization, every render creates a new context value object,
 * causing all consumers to re-render, which triggers parent re-renders,
 * creating an infinite loop.
 */

import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface AppHeaderConfig {
  title: string;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
}

interface AppHeaderContextValue {
  title: string;
  leftActions?: React.ReactNode;
  rightActions?: React.ReactNode;
  setHeader: (config: AppHeaderConfig) => void;
}

const AppHeaderContext = createContext<AppHeaderContextValue | undefined>(undefined);

export const AppHeaderProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [headerConfig, setHeaderConfig] = useState<AppHeaderConfig>({
    title: 'Project Builder',
  });

  // Stable setHeader function - useCallback with empty deps ensures it never changes
  const setHeader = useCallback((config: AppHeaderConfig) => {
    setHeaderConfig(config);
  }, []);

  // CRITICAL: Memoize context value to prevent infinite re-render loops
  // Include all config values, but setHeader is stable (useCallback with empty deps)
  // ReactNode will cause re-renders when they change, but setHeader won't trigger loops
  const value = useMemo<AppHeaderContextValue>(
    () => ({
      title: headerConfig.title,
      leftActions: headerConfig.leftActions,
      rightActions: headerConfig.rightActions,
      setHeader,
    }),
    [headerConfig.title, headerConfig.leftActions, headerConfig.rightActions, setHeader]
  );

  return <AppHeaderContext.Provider value={value}>{children}</AppHeaderContext.Provider>;
};

export const useAppHeader = () => {
  const context = useContext(AppHeaderContext);
  if (context === undefined) {
    throw new Error('useAppHeader must be used within an AppHeaderProvider');
  }
  return context;
};
